'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { ContextNavigation, ListPageSearch } from '@terra/ui-design-system';
import { ListPageGroupByOptionsMenu } from '../../common/ListPageGroupByOptionsMenu';
import { ListPageRefreshButton } from '../../common/ListPageRefreshButton';

type SurfaceAssetsKindItem<Kind> = {
  key: string;
  type: Kind;
  label: string;
};

type SurfaceAssetsContextNavigationProps<Kind, GroupField extends string> = {
  items: ReadonlyArray<SurfaceAssetsKindItem<Kind>>;
  selectedKind: Kind;
  onSelectKind: (kind: Kind) => void;
  getCount: (kind: Kind) => number;
  searchTerm: string;
  onSearchTermChange: (searchTerm: string) => void;
  searchPlaceholder: string;
  onRefresh: () => void;
  isLoading: boolean;
  groupByOptions: ReadonlyArray<{ value: GroupField; label: string }>;
  groupBy: GroupField | null;
  onGroupByChange: (groupBy: GroupField | null) => void;
  areAllGroupsCollapsed: boolean;
  onExpandAllGroups: () => void;
  onCollapseAllGroups: () => void;
  children?: ReactNode;
};

export function SurfaceAssetsContextNavigation<
  Kind,
  GroupField extends string,
>({
  items,
  selectedKind,
  onSelectKind,
  getCount,
  searchTerm,
  onSearchTermChange,
  searchPlaceholder,
  onRefresh,
  isLoading,
  groupByOptions,
  groupBy,
  onGroupByChange,
  areAllGroupsCollapsed,
  onExpandAllGroups,
  onCollapseAllGroups,
  children,
}: SurfaceAssetsContextNavigationProps<Kind, GroupField>) {
  const tChips = useTranslations('proxy.assets.chips');
  const navigationItems = useMemo(
    () =>
      items.map((item) => ({
        value: item.key,
        label: item.label,
        count: getCount(item.type),
      })),
    [getCount, items],
  );
  const selectedKey =
    items.find((item) => item.type === selectedKind)?.key ?? null;

  return (
    <ContextNavigation
      ariaLabel={tChips('label')}
      items={navigationItems}
      value={selectedKey}
      onValueChange={(key) => {
        const item = items.find((candidate) => candidate.key === key);

        if (item) {
          onSelectKind(item.type);
        }
      }}
      actions={
        <>
          <ListPageSearch
            value={searchTerm}
            onValueChange={onSearchTermChange}
            placeholder={searchPlaceholder}
          />
          <ListPageRefreshButton onRefresh={onRefresh} isLoading={isLoading} />
          <ListPageGroupByOptionsMenu
            options={groupByOptions}
            groupByField={groupBy}
            onGroupByFieldChange={onGroupByChange}
            areAllGroupsCollapsed={areAllGroupsCollapsed}
            onExpandAllGroups={onExpandAllGroups}
            onCollapseAllGroups={onCollapseAllGroups}
          >
            {children}
          </ListPageGroupByOptionsMenu>
        </>
      }
    />
  );
}
